import { useState, useMemo, useCallback } from "react";

/**
 * useTableState Hook
 *
 * Manages table state including sorting, filtering, pagination, and column resizing
 *
 * @param {Array} data - Table data
 * @param {Array} columns - Column definitions
 * @param {Object} options - Configuration options
 * @returns {Object} Table state and handlers
 */
const useTableState = (data = [], columns = [], options = {}) => {
  const {
    pageSize: initialPageSize = 50,
    sortable = true,
    searchable = true,
    paginated = true,
    storageKey = "table-state", // Key for localStorage
    variant = "default", // Table variant: default, compact, comfortable
    dataForAutoResize = null, // Optional: different data to measure for auto-resize
    containerRef = null, // Optional: table container ref for viewport-aware sizing
  } = options;

  // Load saved column widths from localStorage
  const loadColumnWidths = useCallback(() => {
    try {
      const saved = localStorage.getItem(`${storageKey}-column-widths`);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error loading column widths:", error);
      return {};
    }
  }, [storageKey]);

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  });
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);

  // Filter data by search term
  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;

    const lowerSearchTerm = searchTerm.toLowerCase();

    return data.filter((row) => {
      return columns.some((column) => {
        if (!column.searchable) return false;

        const value = row[column.field];
        if (value === null || value === undefined) return false;

        return String(value).toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [data, searchTerm, columns, searchable]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortable || !sortConfig.field) return filteredData;

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      // Handle null/undefined
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Handle numbers
      if (typeof aValue === "number" && typeof bValue === "number") {
        return aValue - bValue;
      }

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return aValue.getTime() - bValue.getTime();
      }

      // Handle strings
      return String(aValue).localeCompare(String(bValue));
    });

    return sortConfig.direction === "desc" ? sorted.reverse() : sorted;
  }, [filteredData, sortConfig, sortable]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!paginated) return sortedData;

    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedData.slice(start, end);
  }, [sortedData, currentPage, pageSize, paginated]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!paginated) return 1;
    return Math.ceil(sortedData.length / pageSize);
  }, [sortedData.length, pageSize, paginated]);

  // Handle sort
  const handleSort = useCallback((field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        // Cycle through: asc -> desc -> none
        if (prev.direction === "asc") {
          return { field, direction: "desc" };
        } else if (prev.direction === "desc") {
          return { field: null, direction: null };
        }
      }
      return { field, direction: "asc" };
    });
    setCurrentPage(1); // Reset to first page on sort
  }, []);

  // Handle search
  const handleSearch = useCallback((term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  // Handle page change
  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    },
    [totalPages],
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  }, []);

  // Handle column width change
  const handleColumnResize = useCallback(
    (columnId, width) => {
      setColumnWidths((prev) => {
        const newWidths = {
          ...prev,
          [columnId]: width,
        };

        // Save to localStorage
        try {
          localStorage.setItem(
            `${storageKey}-column-widths`,
            JSON.stringify(newWidths),
          );
        } catch (error) {
          console.error("Error saving column widths:", error);
        }

        return newWidths;
      });
    },
    [storageKey],
  );

  // Calculate responsive padding based on viewport width
  const getResponsivePadding = useCallback(
    (variant) => {
      // Get viewport width (use container if available, otherwise window)
      const viewportWidth =
        containerRef?.current?.offsetWidth || window.innerWidth;

      // Define breakpoints and responsive padding scales
      const BASE_PADDING = {
        compact: {
          mobile: 16,
          tablet: 20,
          desktop: 24,
          wide: 28,
          ultrawide: 32,
        },
        comfortable: {
          mobile: 40,
          tablet: 52,
          desktop: 64,
          wide: 72,
          ultrawide: 80,
        },
        default: {
          mobile: 24,
          tablet: 32,
          desktop: 40,
          wide: 40,
          ultrawide: 48,
        }, // Reduced wide: 48→40, ultrawide: 52→48
        dense: { mobile: 24, tablet: 32, desktop: 40, wide: 40, ultrawide: 48 }, // Reduced wide: 48→40, ultrawide: 52→48
      };

      const paddingScale = BASE_PADDING[variant] || BASE_PADDING.default;

      // Select padding based on viewport width
      // Adjusted breakpoints: 1080p (1920px) now uses "desktop" padding instead of "wide"
      if (viewportWidth <= 480) return paddingScale.mobile;
      if (viewportWidth <= 768) return paddingScale.tablet;
      if (viewportWidth <= 1280) return paddingScale.desktop; // Extended desktop range to include 1080p
      if (viewportWidth <= 1920) return paddingScale.wide; // Wide now starts after 1080p
      return paddingScale.ultrawide;
    },
    [containerRef],
  );

  // Calculate responsive safety buffer
  const getResponsiveSafetyBuffer = useCallback(() => {
    const viewportWidth =
      containerRef?.current?.offsetWidth || window.innerWidth;

    // Scale safety buffer: smaller on mobile, larger on desktop
    // Adjusted to match new padding breakpoints
    if (viewportWidth <= 480) return 4; // Mobile: tight
    if (viewportWidth <= 768) return 6; // Tablet: moderate
    if (viewportWidth <= 1280) return 10; // Desktop: standard (includes 1080p)
    if (viewportWidth <= 1920) return 12; // Wide: generous
    return 14; // Ultrawide: extra generous
  }, [containerRef]);

  // Proportionally distribute column widths to fit viewport
  const fitColumnsToViewport = useCallback(
    (columnWidths, availableWidth) => {
      const columnEntries = Object.entries(columnWidths);

      // Calculate total width needed
      const totalWidth = columnEntries.reduce(
        (sum, [, width]) => sum + width,
        0,
      );

      // If already fits, optionally expand to fill space
      if (totalWidth <= availableWidth) {
        const scaleFactor = availableWidth / totalWidth;

        return columnEntries.reduce((acc, [id, width]) => {
          // Only grow columns that don't have maxWidth constraints
          const column = columns.find((col) => col.id === id);
          const maxWidth = column?.maxWidth || Infinity;
          const scaledWidth = Math.min(width * scaleFactor, maxWidth);

          acc[id] = scaledWidth;
          return acc;
        }, {});
      }

      // Need to shrink: Proportionally reduce all columns
      // But respect minWidth constraints

      // Step 1: Identify columns that can shrink (above minWidth)
      const shrinkableColumns = columnEntries.map(([id, width]) => {
        const column = columns.find((col) => col.id === id);
        const minWidth = column?.minWidth || 100;
        const shrinkableAmount = Math.max(0, width - minWidth);

        return { id, currentWidth: width, minWidth, shrinkableAmount };
      });

      const totalShrinkable = shrinkableColumns.reduce(
        (sum, col) => sum + col.shrinkableAmount,
        0,
      );
      const needToShrink = totalWidth - availableWidth;

      // Step 2: If we can't shrink enough even at minWidths, scale proportionally anyway
      if (totalShrinkable < needToShrink) {
        // Extreme case: viewport too narrow, scale everything proportionally
        const scaleFactor = availableWidth / totalWidth;

        return columnEntries.reduce((acc, [id, width]) => {
          acc[id] = Math.max(50, width * scaleFactor); // Absolute minimum 50px
          return acc;
        }, {});
      }

      // Step 3: Proportionally shrink based on available shrinkable space
      const shrinkRatio = needToShrink / totalShrinkable;

      return shrinkableColumns.reduce((acc, col) => {
        const shrinkAmount = col.shrinkableAmount * shrinkRatio;
        acc[col.id] = col.currentWidth - shrinkAmount;
        return acc;
      }, {});
    },
    [columns],
  );

  // Auto-size columns based on content AND viewport
  const autoSizeColumns = useCallback(
    (allowNaturalWidth = false) => {
      // PHASE 1: Calculate ideal content-based widths
      const idealWidths = {};

      // Get responsive padding values
      const CELL_CSS_PADDING = getResponsivePadding(variant);
      const SAFETY_BUFFER = getResponsiveSafetyBuffer();

      columns.forEach((column) => {
        // Create a temporary canvas to measure text width
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        // Determine content type (defaults to 'text' if not specified)
        const contentType = column.contentType || "text";

        // Set font and padding based on content type
        let font, basePadding, extraPadding, textTransform;

        // Font sizes vary by variant
        const textFontSize =
          variant === "compact"
            ? "12px"
            : variant === "comfortable"
              ? "16px"
              : "14px";
        const badgeFontSize = variant === "comfortable" ? "14px" : "12px";

        switch (contentType) {
          case "badge":
            font = `600 ${badgeFontSize} sans-serif`;
            basePadding = CELL_CSS_PADDING + SAFETY_BUFFER;
            extraPadding = 32; // Badge internal padding (16px) + border + letter-spacing + buffer
            textTransform = "uppercase";
            break;

          case "icon":
            font = textFontSize + " sans-serif";
            basePadding = CELL_CSS_PADDING + 20; // Extra buffer for centered content
            extraPadding = 0;
            textTransform = "none";
            break;

          case "dropdown-with-button":
            font = textFontSize + " sans-serif";
            basePadding = CELL_CSS_PADDING + SAFETY_BUFFER;
            extraPadding = 40; // Icon button (36px) + gap (4px)
            textTransform = "none";
            break;

          case "dropdown":
            font = textFontSize + " sans-serif";
            basePadding = CELL_CSS_PADDING + SAFETY_BUFFER;
            extraPadding = 40; // Dropdown padding + chevron + borders
            textTransform = "none";
            break;

          case "link":
          case "text":
          case "date":
          case "number":
          default:
            font = textFontSize + " sans-serif";
            basePadding = CELL_CSS_PADDING + SAFETY_BUFFER;
            extraPadding = 0;
            textTransform = "none";
            break;
        }

        context.font = font;

        // Measure header text
        const headerWidth =
          context.measureText(column.label).width + basePadding;

        // Measure data
        const dataToMeasure = dataForAutoResize || paginatedData;
        const maxDataWidth = dataToMeasure.reduce((max, row) => {
          const value = row[column.field];
          if (value === null || value === undefined) return max;

          const rawText = column.formatValue
            ? column.formatValue(value, row)
            : String(value);

          const text =
            textTransform === "uppercase" ? rawText.toUpperCase() : rawText;

          const width =
            context.measureText(text).width + basePadding + extraPadding;
          return Math.max(max, width);
        }, 0);

        // Use the larger of header or data width
        const idealWidth = Math.max(
          column.minWidth || 100,
          Math.min(Math.max(headerWidth, maxDataWidth), column.maxWidth || 500),
        );

        idealWidths[column.id] = idealWidth;
      });

      // PHASE 2: Get available viewport width
      let availableWidth;

      if (containerRef?.current) {
        // Use actual container width (accounting for scrollbars, padding, etc.)
        const containerElement = containerRef.current;
        const containerStyles = window.getComputedStyle(containerElement);
        const paddingLeft = parseFloat(containerStyles.paddingLeft) || 0;
        const paddingRight = parseFloat(containerStyles.paddingRight) || 0;
        const borderLeft = parseFloat(containerStyles.borderLeftWidth) || 0;
        const borderRight = parseFloat(containerStyles.borderRightWidth) || 0;

        availableWidth =
          containerElement.clientWidth -
          paddingLeft -
          paddingRight -
          borderLeft -
          borderRight;
      } else {
        // Fallback to window width minus sidebar and padding
        const sidebarWidth = 200; // From App.css
        const estimatedPadding = 48; // 24px left + 24px right
        availableWidth =
          window.innerWidth - sidebarWidth - estimatedPadding - 20; // 20px scrollbar buffer
      }

      // PHASE 3: Fit columns to viewport (conditionally)
      let finalWidths;
      if (allowNaturalWidth) {
        // Use natural content widths, allow horizontal scrolling
        finalWidths = idealWidths;
      } else {
        // Compress columns to fit viewport
        finalWidths = fitColumnsToViewport(idealWidths, availableWidth);
      }

      // Apply widths
      setColumnWidths(finalWidths);

      // Save to localStorage
      try {
        localStorage.setItem(
          `${storageKey}-column-widths`,
          JSON.stringify(finalWidths),
        );
      } catch (error) {
        console.error("Error saving column widths:", error);
      }
    },
    [
      columns,
      dataForAutoResize,
      paginatedData,
      storageKey,
      variant,
      containerRef,
      getResponsivePadding,
      getResponsiveSafetyBuffer,
      fitColumnsToViewport,
    ],
  );

  return {
    // Data
    data: paginatedData,
    totalRows: sortedData.length,
    filteredRows: filteredData.length,
    allRowsCount: data.length,

    // Pagination
    currentPage,
    pageSize,
    totalPages,
    handlePageChange,
    handlePageSizeChange,

    // Search
    searchTerm,
    handleSearch,

    // Sort
    sortConfig,
    handleSort,

    // Column widths
    columnWidths,
    handleColumnResize,
    autoSizeColumns,
  };
};

export default useTableState;
